import { useCallback, useEffect, useRef } from 'react';

import {
  createKeyPoseTimingLogger,
  getKeyPoseTimingNow,
  logKeyPoseTiming,
} from '../keyPoseTiming';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { useKeyPoseFrameCaptureActions } from './useKeyPoseCaptureActions';

const CONTROL_FRAME_RECONCILE_DELAY_MS = 16;

export function useControlFrameAutoReconcile() {
  const keyPoseStore = useKeyPoseStore();
  const { addIntervalKeyPoses } = useKeyPoseFrameCaptureActions();
  const addIntervalKeyPosesRef = useRef(addIntervalKeyPoses);
  const autoGenerateControlFramesRef = useRef(
    keyPoseStore.state.controlFrameSettings.autoGenerateControlFrames,
  );
  const pendingRef = useRef(false);
  const scheduledAtRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    addIntervalKeyPosesRef.current = addIntervalKeyPoses;
  }, [addIntervalKeyPoses]);

  useEffect(() => {
    autoGenerateControlFramesRef.current =
      keyPoseStore.state.controlFrameSettings.autoGenerateControlFrames;
  }, [keyPoseStore.state.controlFrameSettings.autoGenerateControlFrames]);

  const scheduleReconcile = useCallback(() => {
    if (timeoutRef.current !== null) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      if (!pendingRef.current) {
        return;
      }

      if (keyPoseStore.state.editing.dragging) {
        logKeyPoseTiming('controlFrameReconcile', 'postponed while dragging', {
          queuedMs: getQueuedDuration(scheduledAtRef.current),
        });
        scheduleReconcile();
        return;
      }

      pendingRef.current = false;
      if (!autoGenerateControlFramesRef.current) {
        logKeyPoseTiming('controlFrameReconcile', 'skipped; disabled', {
          queuedMs: getQueuedDuration(scheduledAtRef.current),
        });
        scheduledAtRef.current = null;
        return;
      }

      const timing = createKeyPoseTimingLogger('controlFrameReconcile.run', {
        queuedMs: getQueuedDuration(scheduledAtRef.current),
      });
      const changedCount = addIntervalKeyPosesRef.current();
      timing.end('completed', { changedCount });
      scheduledAtRef.current = null;
    }, CONTROL_FRAME_RECONCILE_DELAY_MS);
  }, [keyPoseStore]);

  const reconcileControlFramesAfterEdit = useCallback(() => {
    if (!autoGenerateControlFramesRef.current) {
      return;
    }

    pendingRef.current = true;
    scheduledAtRef.current ??= getKeyPoseTimingNow();
    logKeyPoseTiming('controlFrameReconcile', 'scheduled', {
      alreadyQueued: timeoutRef.current !== null,
      dragging: keyPoseStore.state.editing.dragging,
    });
    scheduleReconcile();
  }, [keyPoseStore, scheduleReconcile]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingRef.current = false;
      scheduledAtRef.current = null;
    };
  }, []);

  return { reconcileControlFramesAfterEdit };
}

function getQueuedDuration(scheduledAt: number | null) {
  if (scheduledAt === null) {
    return 0;
  }

  return Math.round((getKeyPoseTimingNow() - scheduledAt) * 100) / 100;
}
